import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { type Group, useGroups } from '../hooks/useGroups';
import { hasAdminRole, hasRole } from '../utils/roles';

const Page = styled.div`
  min-height: 100%;
  padding: clamp(1rem, 2.4vw, 1.75rem);
  background:
    radial-gradient(circle at top left, color-mix(in srgb, var(--primary) 14%, transparent), transparent 34%),
    linear-gradient(180deg, color-mix(in srgb, var(--surface) 88%, transparent), var(--background));
`;

const Shell = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  gap: 1rem;
`;

const HeroCard = styled.section`
  display: grid;
  gap: 0.85rem;
  padding: clamp(1rem, 1.8vw, 1.25rem);
  border-radius: 24px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--surface) 96%, transparent),
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 82%, transparent)
  );
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
`;

const HeroRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeroText = styled.div`
  display: grid;
  gap: 0.45rem;
  max-width: 40rem;
`;

const Eyebrow = styled.span`
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--primary);
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: var(--onSurface);
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  line-height: 1.45;
  font-size: 0.9rem;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TabRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 72%, white);
  width: fit-content;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  border: none;
  border-radius: 999px;
  padding: 0.55rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(135deg, var(--primary), color-mix(in srgb, var(--primary) 72%, black))'
      : 'transparent'};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurfaceVariant)')};
`;

const Button = styled.button<{ $tone?: 'primary' | 'secondary' }>`
  border-radius: 999px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'primary'
        ? 'color-mix(in srgb, var(--primary) 34%, transparent)'
        : 'color-mix(in srgb, var(--outline) 82%, transparent)'};
  background: ${({ $tone }) =>
    $tone === 'primary'
      ? 'linear-gradient(135deg, var(--primary), color-mix(in srgb, var(--primary) 72%, black))'
      : 'color-mix(in srgb, var(--surface) 92%, transparent)'};
  color: ${({ $tone }) => ($tone === 'primary' ? 'var(--onPrimary)' : 'var(--onSurface)')};
  padding: 0.7rem 1.05rem;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
`;

const FiltersCard = styled.section`
  display: grid;
  gap: 0.8rem;
  padding: 1rem;
  border-radius: 20px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 94%, transparent);
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: min(100%, 16rem);
  padding: 0.8rem 0.95rem;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 72%, var(--surface));
  color: var(--onSurface);
  font-size: 0.95rem;
`;

const Select = styled.select`
  min-width: 12rem;
  padding: 0.8rem 0.95rem;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 72%, var(--surface));
  color: var(--onSurface);
  font-size: 0.95rem;
`;

const ResultsMeta = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const GroupGrid = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const GroupCard = styled.article`
  display: grid;
  gap: 0.65rem;
  padding: 0.8rem 0.9rem;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--surface) 96%, transparent),
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 78%, transparent)
  );
`;

const IdentityRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
`;

const GroupAvatar = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--onPrimary);
  background: linear-gradient(135deg, var(--primary), color-mix(in srgb, var(--primary) 72%, black));
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const CardTitleWrap = styled.div`
  display: grid;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  color: var(--onSurface);
  line-height: 1.25;
`;

const CardBody = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.86rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const Badge = styled.span<{ $tone?: 'private' | 'category' | 'meta' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.28rem 0.56rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: ${({ $tone }) =>
    $tone === 'private'
      ? 'color-mix(in srgb, var(--warningContainer, #fef3c7) 78%, white)'
      : $tone === 'category'
      ? 'color-mix(in srgb, var(--primaryContainer, var(--surfaceVariant, var(--surface))) 88%, white)'
      : 'color-mix(in srgb, var(--surfaceVariant, var(--surface)) 86%, white)'};
  color: ${({ $tone }) => ($tone === 'private' ? 'var(--warning, #92400e)' : 'var(--onSurface)')};
`;

const MemberMeta = styled.p`
  margin: 0;
  font-size: 0.79rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const CardMeta = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const EmptyState = styled.section`
  padding: 1.5rem;
  border-radius: 20px;
  border: 1px dashed color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 90%, transparent);
  color: var(--onSurfaceVariant);
`;

const CATEGORY_LABELS: Record<string, string> = {
  'life-stage': 'Life Stage',
  family: 'Family',
  seniors: 'Seniors',
  youth: 'Youth',
  ministry: 'Ministry',
  'bible-study': 'Bible Study',
  prayer: 'Prayer',
  outreach: 'Outreach',
  other: 'Other',
};

const getCategoryLabel = (category?: string | null) => {
  if (!category) return 'Other';
  return CATEGORY_LABELS[category] ?? category;
};

const getAdminNames = (group: Group) => {
  if (!group.admin_names?.length) return 'Admin team to be assigned';
  return group.admin_names.join(', ');
};

const getMembersLabel = (group: Group) => {
  const current = group.current_members ?? 0;
  return group.max_members ? `${current}/${group.max_members}` : `${current}`;
};

const matchesPrivacy = (group: Group, privacy: string) => {
  if (privacy === 'all') return true;
  if (privacy === 'private') return Boolean(group.is_private);
  return !group.is_private;
};

type GroupTab = 'all' | 'mine';

export const CommunityGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const { data: groups = [], isLoading, error } = useGroups(user?.id);
  const [searchValue, setSearchValue] = useState('');
  const [groupTab, setGroupTab] = useState<GroupTab>('all');
  const [privacyFilter, setPrivacyFilter] = useState<'all' | 'public' | 'private'>('all');
  const canOpenAdminTools = hasAdminRole(roles) || hasRole(roles, 'leader');
  const canManageGroups = hasAdminRole(roles);

  const filteredGroups = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    return groups.filter((group) => {
      const matchesMembership = groupTab === 'all' || Boolean(group.is_joined ?? group.isJoined);
      const matchesSearch =
        !normalizedSearch ||
        group.name.toLowerCase().includes(normalizedSearch) ||
        group.description?.toLowerCase().includes(normalizedSearch) ||
        getCategoryLabel(group.category).toLowerCase().includes(normalizedSearch) ||
        group.admin_names?.some((name) => name.toLowerCase().includes(normalizedSearch));

      return matchesMembership && matchesSearch && matchesPrivacy(group, privacyFilter);
    });
  }, [groupTab, groups, privacyFilter, searchValue]);

  return (
    <Page>
      <Shell>
        <HeroCard>
          <HeroRow>
            <HeroText>
              <Eyebrow>Community groups</Eyebrow>
              <Title>Browse every active group in one place.</Title>
              <Subtitle>Find a group fast, then jump into posts, chats, media, docs, and planning.</Subtitle>
            </HeroText>
            <HeroActions>
              {canOpenAdminTools ? (
                <Button type="button" $tone="secondary" onClick={() => navigate('/groups/admin')}>
                  Admin tools
                </Button>
              ) : null}
              <Button
                type="button"
                $tone="primary"
                onClick={() => {
                  setGroupTab('all');
                  setPrivacyFilter('all');
                }}
              >
                Explore groups
              </Button>
            </HeroActions>
          </HeroRow>
        </HeroCard>

        <FiltersCard>
          <TabRow role="tablist" aria-label="Group list filter">
            <TabButton
              type="button"
              role="tab"
              aria-selected={groupTab === 'all'}
              $active={groupTab === 'all'}
              onClick={() => setGroupTab('all')}
            >
              All Groups
            </TabButton>
            <TabButton
              type="button"
              role="tab"
              aria-selected={groupTab === 'mine'}
              $active={groupTab === 'mine'}
              onClick={() => setGroupTab('mine')}
            >
              My Groups
            </TabButton>
          </TabRow>
          <FiltersRow>
            <SearchInput
              type="search"
              placeholder="Search by name, category, description, or admin"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              aria-label="Search groups"
            />
            <Select
              value={privacyFilter}
              onChange={(event) => setPrivacyFilter(event.target.value as 'all' | 'public' | 'private')}
              aria-label="Filter groups by privacy"
            >
              <option value="all">All groups</option>
              <option value="public">Public only</option>
              <option value="private">Private only</option>
            </Select>
          </FiltersRow>
          <ResultsMeta>
            {isLoading
              ? 'Loading groups...'
              : groupTab === 'mine'
                ? `${filteredGroups.length} group${filteredGroups.length === 1 ? '' : 's'} in your list.`
                : `${filteredGroups.length} group${filteredGroups.length === 1 ? '' : 's'} ready to explore.`}
          </ResultsMeta>
        </FiltersCard>

        {error ? (
          <EmptyState>Unable to load groups right now.</EmptyState>
        ) : null}

        {!error ? (
          <GroupGrid>
            {filteredGroups.map((group) => (
              <GroupCard key={group.id}>
                <CardTop>
                  <IdentityRow>
                    <GroupAvatar aria-hidden>{group.name.trim().charAt(0).toUpperCase()}</GroupAvatar>
                    <CardTitleWrap>
                      <CardTitle>{group.name}</CardTitle>
                      <BadgeRow>
                        <Badge $tone="category">{getCategoryLabel(group.category)}</Badge>
                        <Badge $tone={group.is_private ? 'private' : 'meta'}>
                          {group.is_private ? 'Private' : 'Public'}
                        </Badge>
                      </BadgeRow>
                    </CardTitleWrap>
                  </IdentityRow>
                </CardTop>

                <CardBody>{group.description?.trim() || 'Group description coming soon.'}</CardBody>
                <MemberMeta>
                  {getMembersLabel(group)} members • {group.posts_count ?? 0} posts • {(group.media_count ?? 0) + (group.docs_count ?? 0)} media/docs
                </MemberMeta>

                <CardFooter>
                  <CardMeta>Admins: {getAdminNames(group)}</CardMeta>
                  <ActionRow>
                    <Button
                      type="button"
                      $tone="primary"
                      onClick={() => navigate(`/groups/${group.id}`)}
                      aria-label={`Open group ${group.name}`}
                    >
                      Open group
                    </Button>
                    {canManageGroups ? (
                      <Button
                        type="button"
                        $tone="secondary"
                        onClick={() => navigate(`/groups/${group.id}/manage`)}
                        aria-label={`Manage group ${group.name}`}
                      >
                        Manage
                      </Button>
                    ) : null}
                  </ActionRow>
                </CardFooter>
              </GroupCard>
            ))}
          </GroupGrid>
        ) : null}

        {!isLoading && !error && filteredGroups.length === 0 ? (
          <EmptyState>
            {groupTab === 'mine'
              ? 'You have not joined any groups that match this filter yet. Switch to All Groups to discover more.'
              : 'No groups matched that filter. Try a broader search or switch privacy views.'}
          </EmptyState>
        ) : null}
      </Shell>
    </Page>
  );
};

export default CommunityGroupsPage;